import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { meKey } from "@/app/session/me";
import {
  api,
  onSessionChange,
  refreshSession,
  setAccessToken,
  startSession,
  unwrap,
  type Schemas,
  type SessionData,
} from "@/shared/api/client";

export type SessionUser = Schemas["UserOut"];

type SessionState =
  { status: "loading" } | { status: "signed-out" } | { status: "signed-in"; user: SessionUser };

interface SessionContextValue {
  state: SessionState;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<SessionState>({ status: "loading" });

  // Every session (sign-in, refresh, an accepted invitation) arrives here. Its person seeds
  // GET /auth/me, which keeps them and their permissions fresh from then on.
  const showSession = useCallback(
    (session: SessionData | null) => {
      if (session) {
        queryClient.setQueryData(meKey, session.user);
        setState({ status: "signed-in", user: session.user });
      } else {
        queryClient.removeQueries({ queryKey: meKey });
        setState({ status: "signed-out" });
      }
    },
    [queryClient],
  );

  useEffect(() => {
    const unsubscribe = onSessionChange(showSession);
    // Restore the session from the httpOnly refresh cookie (ADR-0005).
    void refreshSession().then((session) => {
      if (session === null) setState({ status: "signed-out" });
    });
    return unsubscribe;
  }, [showSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    startSession(await unwrap(api.POST("/api/v1/auth/sign-in", { body: { email, password } })));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.POST("/api/v1/auth/sign-out");
    } finally {
      setAccessToken(null);
      queryClient.clear();
      setState({ status: "signed-out" });
    }
  }, [queryClient]);

  const value = useMemo(() => ({ state, signIn, signOut }), [state, signIn, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
